import { useCallback, useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type { NodePresence } from '../lib/data';
import { EMPTY_CONNECTOR_REGISTRY, loadConnectorRegistry, type ConnectorRegistrySnapshot } from '../lib/connector-registry';
import {
  GITHUB_MERGE_ROUTE_STATE_LABEL,
  githubMergeRouteRequest,
  githubMergeRoutes,
  saveGithubMergeRouteOnce,
  type GithubMergeRouteRequest,
  type GithubMergeRouteView,
} from '../lib/github-merge-route';

function computerName(nodes: readonly NodePresence[], nodeId: string | null): string {
  if (nodeId === null) return 'No computer';
  return nodes.find((node) => node.node_id === nodeId)?.name ?? 'Unknown computer';
}

/** The canonical route truth, as the connector inventory reports it. */
export function GithubMergeRouteList({ routes, nodes, busy, onDisable }: {
  routes: readonly GithubMergeRouteView[];
  nodes: readonly NodePresence[];
  busy: boolean;
  onDisable: (route: GithubMergeRouteView) => void;
}) {
  if (routes.length === 0) return <p className="muted">No GitHub merge route is configured.</p>;
  return (
    <ul className="stack github-merge-routes" aria-label="GitHub merge routes">
      {routes.map((route) => (
        <li key={route.connectorId} className="row wrap" data-route-state={route.state}>
          <strong>{route.repository}</strong>
          <span>· {computerName(nodes, route.nodeId)}</span>
          <span>· {GITHUB_MERGE_ROUTE_STATE_LABEL[route.state]}</span>
          <small className="muted">(GitHub sign-in: {route.authState}; health: {route.healthStatus})</small>
          {route.state !== 'disabled' && (
            <button type="button" disabled={busy} onClick={() => onDisable(route)}>Disable</button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function GithubMergeRouteSettings({ client, guard, orgId, nodes }: {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
  nodes: readonly NodePresence[];
}) {
  const [registry, setRegistry] = useState<ConnectorRegistrySnapshot>(EMPTY_CONNECTOR_REGISTRY);
  const [repository, setRepository] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setRegistry(await loadConnectorRegistry(client, orgId));
  }, [client, orgId]);
  useEffect(() => { void reload(); }, [reload]);

  const routes = githubMergeRoutes(registry.connectors);
  const enableRequest = githubMergeRouteRequest({ orgId, repository, nodeId: nodeId === '' ? null : nodeId, enabled: true });

  // A direct verified-owner mutation, like the Planner Host: the existing owner step-up gate,
  // exactly one attempt, and a reread of the canonical inventory rather than any replay.
  const write = (request: GithubMergeRouteRequest) => {
    void guard('post_owner_command', async () => {
      setBusy(true);
      setMessage(null);
      try {
        const result = await saveGithubMergeRouteOnce(client, request);
        setMessage(result.message);
        if (result.outcome !== 'rejected') await reload();
      } finally {
        setBusy(false);
      }
    });
  };

  return (
    <section className="stack github-merge-route-settings" aria-label="GitHub merge route">
      <p className="muted">
        One exact repository merges from one exact computer, using that computer&apos;s own signed-in GitHub CLI.
        Brunel stores no GitHub credential, and a route stays pending until that computer proves its sign-in.
      </p>
      {registry.error !== null && <p role="alert">{registry.error}</p>}
      <GithubMergeRouteList
        routes={routes}
        nodes={nodes}
        busy={busy}
        onDisable={(route) => write({ orgId, repository: route.repository, nodeId: route.nodeId, enabled: false })}
      />
      <form className="stack" onSubmit={(event) => { event.preventDefault(); if (enableRequest !== null) write(enableRequest); }}>
        <label className="stack">
          <span>Repository (owner/name)</span>
          <input
            aria-label="GitHub repository owner/name"
            value={repository}
            placeholder="owner/name"
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setRepository(event.target.value)}
          />
        </label>
        <label className="stack">
          <span>Merging computer</span>
          <select aria-label="Merging computer" value={nodeId} onChange={(event) => setNodeId(event.target.value)}>
            <option value="">Choose the exact computer</option>
            {nodes.map((node) => (
              <option key={node.node_id} value={node.node_id}>
                {node.name}{node.status === 'offline' ? ' · offline' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="row wrap">
          <button type="submit" className="primary" disabled={busy || enableRequest === null}>Enable merge route</button>
        </div>
        {message !== null && <p role="alert">{message}</p>}
      </form>
    </section>
  );
}
